import React, { memo, useCallback, useRef } from 'react'
import { TtTrayImage, TtTrayItem } from '@23/react'
import { useSortable } from '@dnd-kit/sortable'
import { useParams } from 'react-router'
import { switchToTray, useFloorState } from '../../../firebase/hooks/useFloor'
import { setChyron, useOverlayState } from '../../../firebase/hooks/useOverlays'
import Toolbar from './Toolbar'
import GutterMenu from './GutterMenu'
import useTrayTracking from './useTrayTracking'
import { useScroll, useScrollRegister } from './ScrollProvider'
// @ts-expect-error — importing js
import usePlaybackIntent from '../../hooks/usePlaybackIntent'
import { TrayCardImage } from '../../../firebase/model/tray-cards/types/image-model'
import { ChyronType } from '../../../firebase/model/chyron-model'
import { removeCard, setActiveCard } from '../../../firebase/hooks/useTray'
import useFeatures from '../../../sharedLegacy/hooks/useFeatures'
import { usePanelScreen } from '../../../firebase/hooks/usePanel'

interface ImageCardProps {
  isActiveCard: boolean
  isPriorToSelectedCard: boolean
  isLastCard: boolean
  position: number
  participantId: number
  card: TrayCardImage
}

const ImageCard = ({
  isActiveCard,
  isPriorToSelectedCard,
  isLastCard,
  position,
  participantId,
  card,
}: ImageCardProps): JSX.Element | null => {
  const floor = useFloorState()
  const trayTracking = useTrayTracking()
  const overlay = useOverlayState()
  const ref = useRef<HTMLTtTrayItemElement>(null)
  const { live_id: liveId } = useParams<{ live_id: string }>()
  const { setPlaybackPauseIntent } = usePlaybackIntent(liveId)
  useScrollRegister(card.id, position)
  const { scrollIntoView } = useScroll()
  const panelScreenState = usePanelScreen()

  const { HasPanelMixedModeEnabled } = useFeatures()

  const { setNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({
      id: card.id,
    })

  const style = isSorting
    ? {
        transform: `translateY(${transform?.y ?? 0}px)`,
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.8 : 1,
      }
    : {}

  const clearSpeakerChyron = useCallback(() => {
    if (overlay?.type === ChyronType.Speaker) {
      setChyron(null)
    }
  }, [overlay?.type])

  const onCardClickHandler = useCallback(() => {
    if (card.thumbnail) {
      trayTracking.activated(card.id)
      setActiveCard(card.id)

      scrollIntoView(card.id)
      setPlaybackPauseIntent()

      const isMixingPanelWithTray =
        floor?.layout === 'panel' && panelScreenState === 'tray'

      if (!HasPanelMixedModeEnabled || !isMixingPanelWithTray) {
        switchToTray()
      }

      clearSpeakerChyron()
    }
  }, [
    HasPanelMixedModeEnabled,
    card.id,
    card.thumbnail,
    clearSpeakerChyron,
    floor?.layout,
    panelScreenState,
    scrollIntoView,
    setPlaybackPauseIntent,
    trayTracking,
  ])

  const getGutterMode = useCallback(() => {
    if (isSorting) return 'disabled'
    if (isLastCard) return 'persisted'
    return 'normal'
  }, [isLastCard, isSorting])

  setNodeRef(ref.current)

  if (card.participantId && +card.participantId !== participantId) {
    return null
  }

  return (
    <TtTrayItem
      data-id={card.id}
      ref={ref}
      style={style}
      gutterDelay={400}
      gutterMode={getGutterMode()}
    >
      <TtTrayImage
        src={card.thumbnail}
        label={card.label}
        selected={isActiveCard}
        disabled={isPriorToSelectedCard}
        onTt-click={(event) => {
          if (event.target === event.currentTarget) {
            onCardClickHandler()
          }
        }}
      >
        <Toolbar
          cardId={card.id}
          onDeleteClick={() => {
            // trayTracking.deleted(card.id)
            removeCard(card.id)
          }}
          isMediaCard
        />
      </TtTrayImage>
      <GutterMenu cardId={card.id} />
    </TtTrayItem>
  )
}

export default memo(ImageCard)
